import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityDefinition, Appointment, Bundle, Coding, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getReferenceString, isResource } from '@medplum/core';
import { View } from 'react-big-calendar';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { AppointmentEvent } from '../types';
import {
  getAppointments,
  isAppointmentStatusOptionSelected,
  isAppointmentVisibleOnCalendar,
  transformAppointmentToEvent,
  VALID_CALENDAR_APPOINTMENT_STATUSES,
} from '../utils/appointments';
import { APPOINTMENT_STATUS_RESCHEDULED } from '@bariendo/lib/constants';
import { getDateRange } from '../utils/common';
import { extractLocationFromAppointment } from '@bariendo/lib/scheduling';
import { getCodeableConceptCode } from '@bariendo/lib/services';
import { useSubscriptionWithLimitHandling } from '@/services/subscriptions';
import { alert } from '@/services/alert';
import { getOperationOutcomeMessage } from '@bariendo/lib/error';
import { APPOINTMENT_TYPES_SYSTEM, ProviderRole } from '@bariendo/lib/constants';
import { expandCodeSystem } from '@bariendo/lib/codesystem';

export interface AppointmentsFilters {
  roles: ProviderRole[];
  practitioners: Reference<Practitioner>[];
  serviceTypes: string[];
  statuses: string[];
  locations: string[];
}

export interface UseAppointmentsOptions {
  enabled: boolean;
  date: Date;
  view: View;
  timezone: string;
  appointmentsFilters: AppointmentsFilters;
}

export interface UseAppointmentsResult {
  events: AppointmentEvent[];
  loading: boolean;
  refresh: () => void;
  getActivityDefinitionForAppointmentType: (code: string | undefined) => ActivityDefinition | undefined;
  activityCodeSystem: Coding[] | null;
}

/**
 * Hook to fetch and manage appointments for the current provider.
 * Returns appointment events formatted for react-big-calendar.
 */
export function useAppointments({
  date,
  view,
  timezone,
  appointmentsFilters,
  enabled,
}: UseAppointmentsOptions): UseAppointmentsResult {
  const medplum = useMedplum();
  const { currentUser } = useCurrentUser();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [activityDefinitions, setActivityDefinitions] = useState<Map<string, ActivityDefinition>>(new Map());
  const [activityCodeSystem, setActivityCodeSystem] = useState<Coding[] | null>(null);
  const [loading, setLoading] = useState(true);
  // Responses can land out of order when the range or filters change quickly, so only the
  // latest request may touch state: the `cancelled` flag in useSlotData, as a counter so that
  // `refresh()` is covered too.
  const latestRequest = useRef(0);
  // One load-failure toast at a time, replaced rather than stacked, and dismissed on recovery.
  const loadErrorToast = useRef<string | null>(null);

  const { startDate, endDate } = useMemo(() => getDateRange(date, view, timezone), [date, view, timezone]);

  const dismissLoadError = useCallback(() => {
    if (loadErrorToast.current) {
      alert.hide(loadErrorToast.current);
      loadErrorToast.current = null;
    }
  }, []);

  const getActivityDefinitionForAppointmentType = useCallback(
    (code: string | undefined): ActivityDefinition | undefined => {
      if (!code) return undefined;
      return activityDefinitions.get(code);
    },
    [activityDefinitions]
  );

  useEffect(() => {
    async function fetchActivityDefinitions() {
      try {
        const definitions = await medplum.searchResources('ActivityDefinition', {
          status: 'active',
          _count: '100',
        });

        const codeSystem = await expandCodeSystem(medplum, APPOINTMENT_TYPES_SYSTEM);

        setActivityCodeSystem(codeSystem);

        const definitionsMap = new Map<string, ActivityDefinition>();
        for (const def of definitions) {
          const code = getCodeableConceptCode(def.code);
          if (code) {
            definitionsMap.set(code, def);
          }
        }
        setActivityDefinitions(definitionsMap);
      } catch (err) {
        console.error('Failed to fetch activity definitions:', err); // eslint-disable-line no-console
      }
    }

    fetchActivityDefinitions();
  }, [medplum]);

  const fetchAppointments = useCallback(async () => {
    if (!currentUser || !enabled) {
      setLoading(false);
      return;
    }

    const requestId = ++latestRequest.current;

    try {
      setLoading(true);

      // Map "rescheduled" to "cancelled" for API - rescheduled appointments have status "cancelled" in FHIR
      const apiStatuses = new Set<string>();
      if (appointmentsFilters.statuses.length > 0) {
        for (const s of appointmentsFilters.statuses) {
          apiStatuses.add(s === APPOINTMENT_STATUS_RESCHEDULED ? 'cancelled' : s);
        }
      }
      const statusParam =
        apiStatuses.size > 0 ? [...apiStatuses].join(',') : VALID_CALENDAR_APPOINTMENT_STATUSES.join(',');

      let results = await getAppointments(medplum, {
        startDate,
        endDate,
        practitioners: appointmentsFilters.practitioners.map((pr) => getReferenceString(pr)).join(','),
        roles: appointmentsFilters.roles.join(','),
        status: statusParam,
        statusFilter: appointmentsFilters.statuses,
      });
      if (requestId !== latestRequest.current) return;

      if (appointmentsFilters.serviceTypes.length > 0) {
        results = results.filter((appointment) =>
          appointmentsFilters.serviceTypes.includes(getCodeableConceptCode(appointment.appointmentType) ?? '')
        );
      }

      if (appointmentsFilters.locations.length > 0) {
        const selectedLocationSet = new Set(appointmentsFilters.locations);
        results = results.filter((appointment) => {
          const appointmentLocations = extractLocationFromAppointment(appointment);
          return appointmentLocations.some((loc) => {
            const ref = isResource(loc) ? getReferenceString(loc) : loc?.reference;
            return ref && selectedLocationSet.has(ref);
          });
        });
      }

      dismissLoadError();
      setAppointments(results);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      // The toast is the page's error channel (SchedulingPage reports its other loads the same
      // way). The stale result is cleared so the grid never contradicts the current filters.
      dismissLoadError();
      loadErrorToast.current = alert.error("Couldn't load appointments: " + getOperationOutcomeMessage(err));
      setAppointments([]);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [medplum, currentUser, startDate, endDate, appointmentsFilters, enabled, dismissLoadError]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleSubscriptionUpdate = useCallback(
    async (bundle: Bundle) => {
      const bundleEntry = bundle.entry?.find((entry) => entry.resource?.resourceType === 'Appointment');

      if (!bundleEntry) {
        return;
      }

      const updatedAppointment = bundleEntry.resource as Appointment;

      // Remove the appointment if it doesn't pass the visibility/filter criteria
      const passesFilter =
        appointmentsFilters.statuses.length > 0
          ? appointmentsFilters.statuses.some((opt) => isAppointmentStatusOptionSelected(updatedAppointment, opt))
          : isAppointmentVisibleOnCalendar(updatedAppointment);
      if (!passesFilter) {
        setAppointments((prev) => prev.filter((prevAppointment) => prevAppointment.id !== updatedAppointment.id));
        return;
      }

      try {
        // Fetch the updated appointment resource to update the calendar in real time
        const apiStatuses = new Set<string>();
        if (appointmentsFilters.statuses.length > 0) {
          for (const s of appointmentsFilters.statuses) {
            apiStatuses.add(s === APPOINTMENT_STATUS_RESCHEDULED ? 'cancelled' : s);
          }
        }
        const statusParam =
          apiStatuses.size > 0 ? [...apiStatuses].join(',') : VALID_CALENDAR_APPOINTMENT_STATUSES.join(',');

        let updatedAppointmentResponse = await getAppointments(medplum, {
          _id: updatedAppointment.id,
          startDate,
          endDate,
          practitioners: appointmentsFilters.practitioners.map((pr) => getReferenceString(pr)).join(','),
          roles: appointmentsFilters.roles.join(','),
          status: statusParam,
          statusFilter: appointmentsFilters.statuses,
        });

        if (appointmentsFilters.serviceTypes.length > 0) {
          updatedAppointmentResponse = updatedAppointmentResponse.filter((appointment) =>
            appointmentsFilters.serviceTypes.includes(getCodeableConceptCode(appointment.appointmentType) ?? '')
          );
        }

        if (appointmentsFilters.locations.length > 0) {
          const selectedLocationSet = new Set(appointmentsFilters.locations);
          updatedAppointmentResponse = updatedAppointmentResponse.filter((appointment) => {
            const appointmentLocations = extractLocationFromAppointment(appointment);
            return appointmentLocations.some((loc) => {
              const ref = isResource(loc) ? getReferenceString(loc) : loc?.reference;
              return ref && selectedLocationSet.has(ref);
            });
          });
        }

        if (updatedAppointmentResponse.length === 0) {
          // Appointment no longer passes filters (e.g. location), remove it
          setAppointments((prev) => prev.filter((prevAppointment) => prevAppointment.id !== updatedAppointment.id));
          return;
        }

        const updatedAppointmentFullData = updatedAppointmentResponse[0];
        setAppointments((prev) => {
          const indexInPrev = prev.findIndex((a) => a.id === updatedAppointmentFullData.id);
          const updated = [...prev];

          const passesFilter =
            appointmentsFilters.statuses.length > 0
              ? appointmentsFilters.statuses.some((opt) =>
                  isAppointmentStatusOptionSelected(updatedAppointmentFullData, opt)
                )
              : isAppointmentVisibleOnCalendar(updatedAppointmentFullData);
          if (indexInPrev !== -1) {
            updated[indexInPrev] = updatedAppointmentFullData;
          } else if (passesFilter) {
            // It is a new appointment, so add it to the list
            updated.push(updatedAppointmentFullData);
          }

          return updated;
        });
      } catch (err) {
        // Keep the calendar live on the raw subscription resource (no resolved participants,
        // encounter or partner) rather than drop the update. The initial load is the surface
        // that reports a refused search; this path only logs it.
        console.error('Failed to refetch an updated appointment:', err); // eslint-disable-line no-console
        const passesLocationFilter =
          appointmentsFilters.locations.length === 0 ||
          (() => {
            const appointmentLocations = extractLocationFromAppointment(updatedAppointment);
            const selectedLocationSet = new Set(appointmentsFilters.locations);
            return appointmentLocations.some((loc) => {
              const ref = isResource(loc) ? getReferenceString(loc) : loc?.reference;
              return ref && selectedLocationSet.has(ref);
            });
          })();

        const passesStatusFilter =
          appointmentsFilters.statuses.length > 0
            ? appointmentsFilters.statuses.some((opt) => isAppointmentStatusOptionSelected(updatedAppointment, opt))
            : isAppointmentVisibleOnCalendar(updatedAppointment);

        setAppointments((prev) => {
          const indexInPrev = prev.findIndex((a) => a.id === updatedAppointment.id);
          if (indexInPrev !== -1) {
            if (!passesStatusFilter || !passesLocationFilter) {
              // Remove the appointment if it no longer passes filters
              return prev.filter((prevAppointment) => prevAppointment.id !== updatedAppointment.id);
            }

            // If the appointment has a valid status, update it
            const updated = [...prev];
            updated[indexInPrev] = updatedAppointment;
            return updated;
          } else if (passesStatusFilter && passesLocationFilter) {
            // It is a new appointment, so add it to the list
            return [...prev, updatedAppointment];
          }

          return prev;
        });
      }
    },
    [startDate, endDate, medplum, appointmentsFilters]
  );

  // Subscribe to appointment changes for the current date range
  const subscriptionCriteria = useMemo(() => {
    // Keep subscription query in sync with initial fetch: include any
    // appointments that overlap the current visible range
    return `Appointment?date=lt${endDate}&end=gt${startDate}`;
  }, [startDate, endDate]);

  useSubscriptionWithLimitHandling(subscriptionCriteria, handleSubscriptionUpdate);

  // Transform appointments to calendar events
  const events = useMemo(() => {
    return appointments.map((appointment) =>
      transformAppointmentToEvent(appointment, getActivityDefinitionForAppointmentType)
    );
  }, [appointments, getActivityDefinitionForAppointmentType]);

  const refresh = useCallback(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  return {
    events,
    loading,
    refresh,
    getActivityDefinitionForAppointmentType,
    activityCodeSystem,
  };
}
